import {computed, inject, Injectable, signal, WritableSignal} from '@angular/core';
import {BaseApiService} from "../../services/base-api-service/base-api.service";
import {environment} from "../../../../environments/environment";
import {User} from "../../models/user";
import {catchError, distinctUntilChanged, filter, finalize, map, Observable, of, shareReplay, switchMap, take, tap} from "rxjs";
import {Router,} from "@angular/router";
import {Facility} from "../../models/facility";
import {Role} from "../roles.enum";
import {ApiStandardReturn} from "../../models/api-standard-return";
import {
  ChooseActiveFacilityDialogComponent
} from "../../../shared/ui/choose-active-facility-dialog/choose-active-facility-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {SystemParamsService} from "../../services/system-params-service/system-params.service";
import {TokenService} from "../token-service/token.service";
import { HttpErrorResponse } from '@angular/common/http';

const TIMEOUT = 200; // milliseconds
const SELECTED_FACILITY_KEY = "ssba.selectedFacility";

/**
 * Responsible for retrieving user from DB and providing user operations.
 * Depends on authentication service for user token - authentication impl (MSAL or mock) does not matter.
 */
@Injectable({
  providedIn: 'root'
})
export class UserService extends BaseApiService {
  // Injectables
  router = inject(Router);
  dialog = inject(MatDialog);
  private tokenService = inject(TokenService);
  private readonly systemParamsService = inject(SystemParamsService);

  private user$: Observable<User | null>;

  // Hide writable signal from consumers
  private _user: WritableSignal<User | null> = signal(null);
  private _loadingUser = signal(false);
  private _activeFacility: WritableSignal<Facility | null> = signal(null);
  private _previousFacility: WritableSignal<Facility | null> = signal(null); // For temp storage of the facility during logout for form cancelled logging

  // Expose non-writable signal
  user = computed(() => this._user());
  facilities = computed(() => this.user()?.facilities);
  loadingUser = this._loadingUser.asReadonly();
  activeFacility = this._activeFacility.asReadonly();
  previousFacility = this._previousFacility.asReadonly();

  /**
   * User role derived from active facility.
   */
  role = computed(() => {
    switch (this.activeFacility()?.registrationStatus.value.toLocaleLowerCase()) {
      case 'registered':
        return Role.REGISTERED
      case 'non-registered':
        return Role.UNREGISTERED
      case 'de-registered':
        return Role.DEREGISTERED
    }
    return undefined;
  });

  constructor() {
    super();

    // only send auth request once token is retrieved
    this.user$ = this.tokenService.tokenReady$.pipe(
      distinctUntilChanged(),
      filter(ready => ready),
      switchMap(() => {
        this._loadingUser.set(true);
        const path = '/auth';

        return this.http.get<ApiStandardReturn<User>>(environment.apiUrl + path).pipe(
          take(1),
          map(response => response.data),
          tap(user => {
            this._user.set(user);
            let selectedFacilityId = sessionStorage.getItem(SELECTED_FACILITY_KEY);
            if (user.facilities.length === 1) {
              this.setActiveFacility(user.facilities[0].uuid!);
            } else if (selectedFacilityId) {
              this.setActiveFacility(selectedFacilityId);
            } else if (user.facilities.length > 1) {
              this.openFacilityDialog();
            }
            this.systemParamsService.loadSystemParams().subscribe();
          }),
          catchError((error: HttpErrorResponse) => {
            this.resetUser();
            const route = error.status.toString().charAt(0) === '4' ? '/unauthorised' : '/login-failed';
            this.router.navigate([route]);
            return of(this.user());
          }),
          finalize(() => {
            setTimeout(() => this._loadingUser.set(false), TIMEOUT); // Ensure ui properly loads
          })
        );
      }),
      shareReplay(1) // share result with observers
    );

    this.user$.subscribe();
  }

  /**
   * Retrieves user observable.
   * @return Observable of the user
   */
  getUser(): Observable<User | null> {
    return this.user$;
  }

  /**
   * Reset user.
   */
  resetUser(): void {
    this._user.set(null);
    this._previousFacility.set(this.activeFacility());
    this._activeFacility.set(null);
    sessionStorage.removeItem(SELECTED_FACILITY_KEY);
  }

  /**
   * Opens dialog to select active facility.
   */
  openFacilityDialog(): void {
    const dialogRef = this.dialog.open(
      ChooseActiveFacilityDialogComponent,
      {
        data: {facilities: this.facilities(), currentActiveFacilityId: this.activeFacility()?.uuid ?? undefined},
        disableClose: true
      }
    );

    dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
      if (result?.facility) {
        const id = result.facility.uuid;
        sessionStorage.setItem(SELECTED_FACILITY_KEY, id);
        this.router.navigate(['/change-active-facility', id]);
      }
    })
  }

  /**
   * Sets active facility if it belongs to the user.
   * @param id Facility ID
   */
  setActiveFacility(id: string): void {
    const matchedFacility = this.facilities()?.filter(facility => facility.uuid === id)[0];

    if (matchedFacility) {
      this._activeFacility.set(matchedFacility);
    }
  }

  /**
   * Resets active facility.
   */
  resetActiveFacility(): void {
    this._activeFacility.set(null);
  }
}
